import { Satisfaction } from 'lib-shared-legacy-types';
import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';

import { SatisfactionState } from 'components/settings/reducers/sections/satisfaction';

import { LanguageCustomizationForm as DixaMessengerLanguageCustomizationForm } from '../DixaMessengerCSAT/CustomizationPage/types';
import { DixaMessengerPanelForms } from '../DixaMessengerCSAT/Panel/messengerPanelForms';
import { OwnProps } from '../EmailCSAT/CustomizationPage/CustomizationPage';
import { LanguageCustomizationForm } from '../EmailCSAT/CustomizationPage/types';
import { EmailPanelForms } from '../EmailCSAT/EmailPanel/emailPanelForms';

type ChannelsCustomization =
  | Satisfaction.ChatRatingCustomization
  | Satisfaction.EmailRatingCustomization
  | Satisfaction.DixaMessengerRatingCustomization;

const getLanguageCustomizationSettings =
  (channel: Satisfaction.CSATChannelsLevel) =>
  (
    state: CustomizationPageSelectors.State,
    props: OwnProps | { settingsId: string | null },
  ): ChannelsCustomization | null => {
    let initialValues:
      | Satisfaction.EmailCsatSettings
      | Satisfaction.ChatCsatSettings
      | Satisfaction.DixaMessengerCsatSettings
      | null = null;

    const channelCustomization:
      | SatisfactionState['customization']['messenger']
      | SatisfactionState['customization']['email']
      | SatisfactionState['customization']['widgetchat'] =
      state.settings.satisfaction.customization[channel];

    initialValues =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      channelCustomization.languagesLevel.values?.find(
        ({ id }) => id === props.settingsId,
      ) || null;

    if (initialValues) {
      return initialValues.customization;
    }

    // go up the chain of settings to find the initial values
    const contactLevelSettings =
      channelCustomization.contactPointsLevel.values?.[0];

    if (contactLevelSettings) {
      return contactLevelSettings.customization;
    }

    // go up the chain of settings even further to find the initial values
    const globalSettings = channelCustomization.globalLevel.values;

    if (globalSettings) {
      return globalSettings.customization;
    }

    // no values found in the parent levels
    return null;
  };

function getEmailLanguageCustomizationFormValues(
  state: CustomizationPageSelectors.State,
): LanguageCustomizationForm.FormValues {
  const selectValue = formValueSelector(
    EmailPanelForms.LanguageCustomizationPageForm,
  );

  const formValues: LanguageCustomizationForm.FormValues = {
    body: selectValue(state, LanguageCustomizationForm.Fields.body) || '',
    headerText:
      selectValue(state, LanguageCustomizationForm.Fields.headerText) || '',
    ratingStyle:
      selectValue(state, LanguageCustomizationForm.Fields.ratingStyle) ||
      'emojis',
    subject: selectValue(state, LanguageCustomizationForm.Fields.subject) || '',
    subheaderText:
      selectValue(state, LanguageCustomizationForm.Fields.subheaderText) || '',
    placeholderText:
      selectValue(state, LanguageCustomizationForm.Fields.placeholderText) ||
      '',
    ratingQuestion:
      selectValue(state, LanguageCustomizationForm.Fields.ratingQuestion) || '',
    actionButtonText:
      selectValue(state, LanguageCustomizationForm.Fields.actionButtonText) ||
      '',
  };
  return formValues;
}

function getDixaMessengerLanguageCustomizationFormValues(
  state: CustomizationPageSelectors.State,
): DixaMessengerLanguageCustomizationForm.FormValues {
  const selectValue = formValueSelector(
    DixaMessengerPanelForms.LanguageCustomizationPageForm,
  );

  const formValues: DixaMessengerLanguageCustomizationForm.FormValues = {
    look_and_feel:
      selectValue(
        state,
        DixaMessengerLanguageCustomizationForm.Fields.lookAndFeel,
      ) || 'emojis',
    rating_question:
      selectValue(
        state,
        DixaMessengerLanguageCustomizationForm.Fields.ratingQuestion,
      ) || '',
    rating_follow_up_question:
      selectValue(
        state,
        DixaMessengerLanguageCustomizationForm.Fields.followupQuestion,
      ) || '',
    rating_placeholder_text:
      selectValue(
        state,
        DixaMessengerLanguageCustomizationForm.Fields.ratingPlaceholder,
      ) || '',
    rating_completed_message:
      selectValue(
        state,
        DixaMessengerLanguageCustomizationForm.Fields.thankyouMessage,
      ) || '',
  };
  return formValues;
}

function getFormInitialChatValuesFromState(
  customization: Satisfaction.ChatRatingCustomization | null,
): Partial<Satisfaction.ChatRatingCustomization> {
  return customization || {};
}

function getFormInitialEmailValuesFromState(
  customization: Satisfaction.EmailRatingCustomization | null,
): Partial<LanguageCustomizationForm.FormValues> {
  if (!customization) {
    return {};
  }

  return {
    body: customization.body || undefined,
    subject: customization.subject || undefined,
    actionButtonText: customization.button_text || undefined,
    headerText: customization.thank_you_message || undefined,
    placeholderText: customization.placeholder_text || undefined,
    ratingQuestion: customization.rating_question || undefined,
    ratingStyle: customization.look_and_feel || undefined,
    subheaderText: customization.comment_question || undefined,
  };
}

function getFormInitialDixaMessengerValuesFromState(
  customization: Partial<Satisfaction.DixaMessengerRatingCustomization> | null,
): Partial<DixaMessengerLanguageCustomizationForm.FormValues> {
  if (!customization) {
    return {};
  }

  return {
    look_and_feel: customization.look_and_feel || undefined,
    rating_question: customization.rating_question || undefined,
    rating_follow_up_question:
      customization.rating_follow_up_question || undefined,
    rating_placeholder_text: customization.rating_placeholder_text || undefined,
    rating_completed_message:
      customization.rating_completed_message || undefined,
  };
}
export namespace CustomizationPageSelectors {
  export type State = { settings: { satisfaction: SatisfactionState } };

  export const emailLanguageCustomizationFormValuesgetter =
    getEmailLanguageCustomizationFormValues;
  export const dixaMessengerLanguageCustomizationFormValuesgetter =
    getDixaMessengerLanguageCustomizationFormValues;

  export const initialEmailValuesSelector = createSelector(
    getLanguageCustomizationSettings('email'),
    getFormInitialEmailValuesFromState,
  );

  export const initialChatValuesSelector = createSelector(
    getLanguageCustomizationSettings('widgetchat'),
    getFormInitialChatValuesFromState,
  );

  export const initialDixaMessengerValuesSelector = createSelector(
    getLanguageCustomizationSettings('messenger'),
    getFormInitialDixaMessengerValuesFromState,
  );
}
